import React, { useState } from 'react';
import { triggerBlobDownload, extractFilename } from '../../../../utilities/download';
import { get } from 'lodash';
import axios from 'axios';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfFocusPane,
	SdfQuickStat,
	SdfShimmer
} from '@synerg/react-components';
import { SearchHeader } from './searchHeader';
import { LinkAndCutOff } from './linkAndCutoff';
import { RequestAuthGrid } from './requestAuthGrid';
import { DeniedAuthGrid } from './deniedAuthGrid';
import { AllowedAuthGrid } from './allowedAuthGrid';
import { ModifiyScope } from './modifyScope';

const SubscriptionsDetails = (props = {}) => {
	const { projectDetails, projectID, prodProjectID } = props || {};
	// const projectID = projectListData.projectAppId;
	// const prodProjectID = projectListData.type === "PROD" ? projectListData.projectAppId : "";

	// common states
	const [loading, setLoading] = useState(false);
	const [isError, setError] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [infoMessage, setInfoMessage] = useState(null);

	// segment states
	const [selectedEnvironment, setSelectedEnvironment] = useState('PROD');
	const [activeChangeRequest, setActiveChangeRequest] = useState({});
	const [showEmailButton, setShowEmailButton] = useState(false);
	const [cancelableRequest, setCancelableRequest] = useState(false);
	const [carDetails, setCarDetails] = useState({});
	const [totalSubscription, setTotalSubscription] = useState(0);

	// Shared Header Search
	const [searchedText, setSearchedText] = useState('');
	const [searchedStatus, setSearchedStatus] = useState([]);

	// Subscriber Info Modal
	const [isModalVisible, setModalVisibility] = useState(false);
	const [modalData, setModalData] = useState({});

	// modify scope
	const [loadModifyScope, setLoadModifyScope] = useState(false);
	const [modifyScopeSuccess, setModifyScopeSuccess] = useState(false);

	// API CALLS
	const getActiveCR = async (selectedProjectId) => {
		try {
			setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: false});
			const { data: { success = false, changeRequest } } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/project/${selectedProjectId}/change-request/active`
			);

			if (success) {
				setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: changeRequest});
				if (
					get(changeRequest, 'preAuthRequired', false) &&
					get(changeRequest, 'applicationType', '') === 'B2B' &&
					get(changeRequest, 'environment', '') === 'PROD'
				) {
					setShowEmailButton(true);
				}
			}
		} catch (e) {
			setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: false});
		}
	};

	const getCarDetails = async (paginationDetails = {}, environment = 'PROD') => {
		const {
			searchText,
			selectedSubscriptions,
			requestFor
		} = paginationDetails;
		try {
			const params = {
				removeOwnOrgSubs: true,
				projectType: environment,
				search: searchText || null,
				subscriptions: selectedSubscriptions.map(item => item.value) || [],
				type: requestFor || "all"
			};
			const availableProjectID = projectID || prodProjectID;
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/car-app-details/${availableProjectID}`, {
				params,
				cancelToken: getCancelableRequestToken().token
			});
			setCarDetails(data);
			if (environment === 'PROD') {
				setTotalSubscription(get(data, 'prod.subscriptionsTotal', 0));
			} else {
				setTotalSubscription(get(data, 'subscriptionsTotal', 0));
			}
		} catch (e) {
			if (e && e.message !== 'search request canceled') {
			}
		}
	};

	const getSubscriberDetails = async (requestPayload) => {
		startAPICall();
		try {
			const { data } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/project/${projectDetails._id + "/associate/" + requestPayload}`
			);
			setModalData(data || {});
			setModalVisibility(true);
		} catch (e) {
			setModalData({});
			setError(true);
			setShowToast(true);
			setInfoMessage('Unable to retrieve subscriber information.');
      timer(5000).then( () => setShowToast(false));
		} finally {
			setLoading(false);
		}
	};

	const downloadReport = async () => {
    try {
      startAPICall();
			const pId = selectedEnvironment === 'PROD' ? prodProjectID : projectID;

      axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/${pId}/subscriptions-download/generate`,
        null, {
          cancelToken: getCancelableRequestToken().token,
          params: {
            projectId: selectedEnvironment === 'PROD' ? prodProjectID : projectID,
          }
        })
        .then(async (response) => {
          if (response.data.downloadUrl) {
            setShowToast(true);
            setInfoMessage('Found cached file.');
            return getFile(response.data.downloadUrl);
          } else if (response.data.trackingUrl) {
            await getStatus(response.data.trackingUrl);
          } else {
            throw new Error('Error generating the file.');
          }
        })        
    } catch (e) {
        const errorMessage = get(e, 'response.data.message', 'There was an error while downloading the file.');
        setError(true);
        setShowToast(true);
        setInfoMessage(errorMessage);
        setLoading(false);
        timer(5000).then( () => setShowToast(false))
    }
	};

	const getStatus = async (trackingUrl) => {
		try {
			let i = 0;
			while (i++ < 1000) {
				const {
					data: { percentComplete = 0, minutesToComplete = 0, downloadUrl = null,  }
				} = await axios.get(`${import.meta.env.VITE_ROOT_PATH}${trackingUrl}`, {
					cancelToken: getCancelableRequestToken().token,
					timeout: 45000,
				});

        setShowToast(true);
				if (downloadUrl) {
					setInfoMessage( 'File is ready.');
					return getFile(downloadUrl);

				} else {
          let message = `${percentComplete}% completed.`
          if ( percentComplete > 0) {
            message = message + ' Time to completion: ' + (minutesToComplete >= 2 ? `${minutesToComplete} minutes.` : 'less than a minute.');
          }
					setInfoMessage(message);
				}

				await timer(1000);
			}
		} catch (e) {
			const errorMessage = e.response.data.message || 'There was an error generating the file.';
			setError(true);
			setShowToast(true);
			setInfoMessage(errorMessage);
			setLoading(false);
      timer(5000).then( () => setShowToast(false));
			return {};
		}
	};

	const getFile = async (downloadUrl) => {
		await axios.get(`${import.meta.env.VITE_ROOT_PATH}${downloadUrl}`,{
			cancelToken: getCancelableRequestToken().token,
			responseType: "blob",
		})
		.then((response) => {
			downloadFile(response);
			setShowToast(true);
			setInfoMessage('Receiving file.');
      timer(5000).then( () => setShowToast(false)); 
			setLoading(false);
		})
		.catch((e) => {
			const errorMessage = get(e, 'response.data.message', 'Error');
			setError(true);
			setShowToast(true);
			setInfoMessage(errorMessage);
			setLoading(false);
      timer(5000).then( () => setShowToast(false));
		});
	};

	const downloadFile = async (reportFile) => {
    setShowToast(false);
		triggerBlobDownload(new Blob([reportFile.data]), extractFilename(reportFile.headers["content-disposition"]));
	};

	// UTILS METHODS
	const startAPICall = () => {
		setLoading(true);
		setError(false);
		setShowToast(false);
	};

	const getCancelableRequestToken = () => {
		if (cancelableRequest) {
			cancelableRequest.cancel('search request canceled');
		}
		const newRequest = axios.CancelToken.source();
		setCancelableRequest(newRequest);
		return newRequest;
	};

	const timer = (ms) => new Promise((res) => setTimeout(res, ms));

	const closeModal = () => {
		setModalVisibility(false);
	};

	const subscriberEmailID = (data = {}) => {
		if(data && data.associateContactInfoCollection && data.associateContactInfoCollection.associateContactInfo) {
			const associateContactInfo = data.associateContactInfoCollection.associateContactInfo;
			if(Array.isArray(associateContactInfo)) {
				if(associateContactInfo[0]) {
					const firstContact = associateContactInfo[0];
					if(firstContact && firstContact.contactInfo) {
						if(firstContact.contactInfo.includes('@')){
							return firstContact.contactInfo;
						}
					}
				}
				if(associateContactInfo[1]) {
					const secondContact = associateContactInfo[1];
					if (secondContact && secondContact.contactInfo) {
						if (secondContact.contactInfo.includes("@")) {
							return secondContact.contactInfo;
						}
          }
				}
			}
		}
		return '';
	}

	return (
    <div id="subscription-container">
      {loading ? (
        <SdfBusyIndicator
          style={{ color: "#1a8099" }}
          size="xl"
          fullScreen={true}
          overlay={true}
        />
      ) : null}
      <SdfAlert
        className="fixed min-w-1/3"
        style={{ top: "6rem", right: "calc(100% - 66%)", zIndex: "9999" }}
        hidden={!showToast}
        autoClose={false}
        // autoCloseAfter={5000}
        useAnimation={true}
        status={isError ? "error" : "success"}
      >
        {infoMessage}
      </SdfAlert>
      <div className="flex justify-between">
        <div className="text-heading-05 font-bold">Subscriptions</div>
        <div>
          <SdfButton
            emphasis="secondary"
            className="pr-2"
            disabled={totalSubscription < 1 ? true : undefined}
            onClick={downloadReport}
          >
            Download report
          </SdfButton>
          <SdfButton
            emphasis="primary"
            onClick={() => setLoadModifyScope(true)}
          >
            Modify app scope
          </SdfButton>
        </div>
      </div>
      <SearchHeader {...{
				projectID, prodProjectID, projectDetails, getActiveCR, modifyScopeSuccess, getCarDetails,
				selectedEnvironment, setSelectedEnvironment, searchedText, setSearchedText, searchedStatus,
				setSearchedStatus
			}} />
      <LinkAndCutOff
        selectedEnvironment={selectedEnvironment}
        activeChangeRequest={activeChangeRequest[selectedEnvironment]}
        projectDetails={projectDetails}
        startAPICall={startAPICall}
        setLoading={setLoading}
        setError={setError}
        setShowToast={setShowToast}
        setInfoMessage={setInfoMessage}
        carDetails={carDetails}
      />
      <RequestAuthGrid {...{
				data: carDetails,
				activeCR: activeChangeRequest[selectedEnvironment],
				showEmailButton,
				selectedEnvironment,
				getSubscriberDetails,
				searchedText,
				searchedStatus
			}} />
      <DeniedAuthGrid {...{
				data: carDetails,
				selectedEnvironment,
				getSubscriberDetails,
				searchedText,
				searchedStatus
			}} />
      <AllowedAuthGrid {...{
				data: carDetails,
				selectedEnvironment,
				getSubscriberDetails,
				searchedText,
				searchedStatus
			}} />
      {loadModifyScope && (
        <ModifiyScope
          projectID={projectID}
          prodProjectID={prodProjectID}
          projectDetails={projectDetails}
          loadModifyScope={loadModifyScope}
          setLoadModifyScope={setLoadModifyScope}
          activeChangeRequest={activeChangeRequest}
          carDetails={carDetails}
          setModifyScopeSuccess={setModifyScopeSuccess}
        />
      )}
      <SdfFocusPane
        visible={isModalVisible}
        acceptLabel="Close"
        closeable={true}
        dismissOnClickOutside={true}
        hideDismissButton={true}
				onSdfAccept={() => closeModal() }
        onSdfDismiss={() => closeModal() }
        size="sm"
      >
        <div slot="title" className="mt-10 ml-5">
          Subscriber Info
        </div>
        <div className="px-10">
          <div className="text-body-02 mb-5">
            As the subscriber of this application, this person is authorized to
            provide data consent for the organization.
          </div>

          <div className="text-heading-05 font-bold">
            Practitioner contact details
          </div>

          <div className="my-5">
            <SdfQuickStat className="mb-4" size="sm" label="Name">
              {loading ? (
                <SdfShimmer />
              ) : modalData.name ? (
                modalData.name
              ) : (
                "N/A"
              )}
            </SdfQuickStat>

            <SdfQuickStat className="mb-4" size="sm" label="Email">
              {loading ? (
                <SdfShimmer />
              ) : subscriberEmailID(modalData) ?
				subscriberEmailID(modalData) : (
                "N/A"
              )}
            </SdfQuickStat>
            <SdfQuickStat className="mb-4" size="sm" label="Subscriber AOID">
              {loading ? (
                <SdfShimmer />
              ) : modalData.associateOid ? (
                modalData.associateOid
              ) : (
                "N/A"
              )}
            </SdfQuickStat>
          </div>
        </div>
      </SdfFocusPane>
    </div>
  );
};

export default SubscriptionsDetails;
